// Fonction pour calculer le taux de complétion d'un client
export function calculateClientCompletion(client) {

  // **FONCTION UTILITAIRE : Vérifier si un champ est vide/manquant**
  const isEmpty = (value) => {
    return value === undefined ||
           value === null ||
           value === '' ||
           value === '{}' ||
           (Array.isArray(value) && value.length === 0);
  };

  // **FONCTION UTILITAIRE : Vérifier si un boolean est défini**
  const isBooleanDefined = (value) => {
    return value === true || value === false;
  };

  const completionChecks = {
    // **INFORMATIONS DE BASE (25%)**
    basic: {
      weight: 25,
      fields: [
        { name: 'Nom du client', value: client.name, required: true },
        { name: 'Description', value: client.description, required: false },
        { name: 'Environnement', value: client.environment, required: true },
        { name: 'Maintenance à distance', value: client.remoteMaintenance, required: true, isBoolean: true },
      ]
    },
    
    // **URLs ET ACCESSIBILITÉ (20%)**
    urls: {
      weight: 20,
      fields: [
        { name: 'URL portail patient', value: client.patientPortalUrl, required: false },
        { name: 'URL portail soignant', value: client.carePortalUrl, required: false },
        { name: 'Accessibilité patient externe', value: client.patientPortalUrlExternallyAccessible, required: false, isBoolean: true },
        { name: 'Accessibilité soignant externe', value: client.carePortalUrlExternallyAccessible, required: false, isBoolean: true },
      ]
    },
    
    // **PROCÉDURES ET CONFIGURATION (15%)**
    procedures: {
      weight: 15,
      fields: [
        { name: 'Procédure de connexion', value: client.connectionProcedure, required: false },
        { name: 'Canal Slack', value: client.slackChannelId, required: false },
      ]
    },
    
    // **SERVEURS - Présence (40%)**
    servers: {
      weight: 40,
      fields: [
        { name: 'Présence de serveurs', value: client.servers && client.servers.length > 0, required: true, isBoolean: true },
      ]
    }
  };
  
  // NOTE: Bastion et télémaintenance sont EXCLUS du calcul car optionnels
  
  // **ANALYSER LES SERVEURS EN DÉTAIL**
  let serverAnalysis = {
    totalServers: 0,
    incompleteServers: [],
    criticalIssues: [],
    warningIssues: [],
    completionScore: 0,
    totalServerFields: 0,
    completedServerFields: 0
  };
  
  if (client.servers && client.servers.length > 0) {
    serverAnalysis.totalServers = client.servers.length;
    let allServerFields = [];
    
    client.servers.forEach((server, index) => {
      const serverPrefix = client.servers.length > 1 ? `Serveur ${index + 1}: ` : '';
      let serverIssues = [];
      let serverCritical = [];
      let serverWarnings = [];
      
      // **INFORMATIONS CRITIQUES** (obligatoires pour un serveur fonctionnel)
      const criticalFields = [
        { name: 'Nom/IP', value: server.name, field: `${serverPrefix}Nom/IP` },
        { name: 'OS', value: server.os, field: `${serverPrefix}OS` },
        { name: 'Type de serveur', value: server.type, field: `${serverPrefix}Type` },
        // ÉTATS CRITIQUES (sonde et optimisation BDD sont critiques)
        { name: 'Sonde déployée', value: server.probeDeployed, field: `${serverPrefix}Sonde déployée`, isBoolean: true }
      ];
      
      // BDD optimisée seulement si ce n'est PAS un serveur applicatif
      if (server.type !== 'Serveur applicatif') {
        criticalFields.push({
          name: 'BDD optimisée', 
          value: server.dbOptimized, 
          field: `${serverPrefix}BDD optimisée`, 
          isBoolean: true
        });
      }
      
      criticalFields.forEach(check => {
        const isValid = check.isBoolean ? isBooleanDefined(check.value) : !isEmpty(check.value);
        if (!isValid) {
          serverCritical.push(check.name);
          serverIssues.push(check.field);
        }
        allServerFields.push({ name: check.field, value: isValid, required: true });
      });
      
      // **CONFIGURATION MATÉRIELLE** (importante mais pas critique)
      const hardwareFields = [
        { name: 'RAM', value: server.ram, field: `${serverPrefix}RAM` },
        { name: 'CPU', value: server.cpu, field: `${serverPrefix}CPU` },
        { name: 'Espace disque', value: server.diskSpace, field: `${serverPrefix}Espace disque` }
      ];
      
      hardwareFields.forEach(check => {
        const isValid = !isEmpty(check.value);
        if (!isValid) {
          serverWarnings.push(check.name);
        }
        allServerFields.push({ name: check.field, value: isValid, required: false });
      });
      
      // **DOSSIERS D'INSTALLATION** (importants)
      const folderFields = [
        { name: 'Dossier APP', value: server.appInstallFolder, field: `${serverPrefix}Dossier APP` },
        { name: 'Dossier BDD', value: server.dbInstallFolder, field: `${serverPrefix}Dossier BDD` }
      ];
      
      // Le dossier BDD n'est requis que si ce n'est pas un serveur applicatif pur
      folderFields.forEach(check => {
        if (check.name === 'Dossier BDD' && server.type === 'Serveur applicatif') {
          return; // Skip pour serveur applicatif
        }
        const isValid = !isEmpty(check.value);
        if (!isValid) {
          serverWarnings.push(check.name);
        }
        allServerFields.push({ name: check.field, value: isValid, required: false });
      });
      
      // **VERSIONS LOGICIELLES** (importantes pour le suivi)
      const softwareFields = [
        { name: 'MariaDB', value: server.mariadbVersion, field: `${serverPrefix}MariaDB`, skipIf: server.type === 'Serveur applicatif' },
        { name: 'Apache', value: server.apacheVersion, field: `${serverPrefix}Apache`, skipIf: server.type === 'Serveur base de données' },
        { name: 'Node.js', value: server.nodejsVersion, field: `${serverPrefix}Node.js` }
      ];
      
      softwareFields.forEach(check => {
        if (check.skipIf) return; // Skip si condition
        const isValid = !isEmpty(check.value);
        if (!isValid) {
          serverWarnings.push(check.name);
        }
        allServerFields.push({ name: check.field, value: isValid, required: false });
      });
      
      // **APPLICATIONS MÉTIERS** (importantes pour le suivi des versions)
      const appFields = [
        { name: 'App Patient', value: server.gitPatientAppVersion, field: `${serverPrefix}App Patient`,
          skipIf: server.type === 'Serveur base de données' || server.type === 'Serveur interop' },
        { name: 'Manager Front', value: server.gitManagerFrontAppVersion, field: `${serverPrefix}Manager Front`,
          skipIf: server.type === 'Serveur base de données' || server.type === 'Serveur interop' },
        { name: 'Marshaller', value: server.gitMarshallerVersion, field: `${serverPrefix}Marshaller`,
          skipIf: server.type === 'Serveur base de données' },
        { name: 'Marshaller Interop', value: server.gitMarshallerInteropVersion, field: `${serverPrefix}Marshaller Interop`,
          skipIf: server.type !== 'Serveur interop' && server.type !== 'Serveur tout-en-un' }
      ];
      
      appFields.forEach(check => {
        if (check.skipIf) return; // Skip si condition
        const isValid = !isEmpty(check.value);
        if (!isValid) {
          serverWarnings.push(check.name);
        }
        allServerFields.push({ name: check.field, value: isValid, required: false });
      });
      
      // **SSH ACCESS** (si activé)
      if (server.sshAccessible) {
        const sshFields = [
          { name: 'Utilisateur SSH', value: server.sshUsername, field: `${serverPrefix}Utilisateur SSH` },
          { name: 'Port SSH', value: server.sshPort, field: `${serverPrefix}Port SSH` }
        ];
        
        sshFields.forEach(check => {
          const isValid = !isEmpty(check.value);
          if (!isValid) {
            serverWarnings.push(check.name);
          }
          allServerFields.push({ name: check.field, value: isValid, required: false });
        });
      }
      
      // **ANALYSER LE SERVEUR**
      // Un serveur est considéré comme "incomplet" seulement si :
      // - Il a des problèmes critiques OU
      // - Il a plus de 3 warnings (beaucoup de champs manquants)
      const hasSignificantIssues = serverCritical.length > 0 || serverWarnings.length > 3;
      
      if (hasSignificantIssues) {
        serverAnalysis.incompleteServers.push({
          index: index + 1,
          name: server.name || `Serveur ${index + 1}`,
          critical: serverCritical,
          warnings: serverWarnings,
          totalIssues: serverCritical.length + serverWarnings.length,
          reason: serverCritical.length > 0 ? 'critical' : 'many_warnings'
        });
      }
      
      // **AJOUTER AUX PROBLÈMES GLOBAUX**
      serverAnalysis.criticalIssues = serverAnalysis.criticalIssues.concat(
        serverCritical.map(issue => `${serverPrefix}${issue}`)
      );
      serverAnalysis.warningIssues = serverAnalysis.warningIssues.concat(
        serverWarnings.map(issue => `${serverPrefix}${issue}`)
      );
    });
    
    // **CALCULER LE SCORE DE COMPLÉTION DES SERVEURS**
    serverAnalysis.totalServerFields = allServerFields.length;
    serverAnalysis.completedServerFields = allServerFields.filter(field => field.value === true).length;
    serverAnalysis.completionScore = serverAnalysis.totalServerFields > 0 ? 
      Math.round((serverAnalysis.completedServerFields / serverAnalysis.totalServerFields) * 100) : 0;
    
    // **AJOUTER LES CHAMPS SERVEURS AU CALCUL PRINCIPAL**
    completionChecks.serverDetails = {
      weight: 0, // Le poids est déjà compté dans servers
      fields: allServerFields
    };
  }
  
  // **CALCULER LE SCORE DE COMPLÉTION GLOBAL**
  let totalScore = 0;
  let maxScore = 0;
  let missingFields = [];
  let completedSections = {};
  let criticalMissing = [];
  
  Object.entries(completionChecks).forEach(([section, config]) => {
    let sectionScore = 0;
    let sectionMaxScore = 0;
    let sectionMissing = [];
    
    config.fields.forEach(field => {
      const fieldWeight = field.required ? 2 : 1;
      sectionMaxScore += fieldWeight;
      
      // Vérifier si le champ est valide
      let isValid = false;
      if (field.isBoolean) {
        isValid = isBooleanDefined(field.value);
      } else {
        isValid = !isEmpty(field.value);
      }
      
      if (isValid) {
        sectionScore += fieldWeight;
      } else {
        sectionMissing.push(field.name);
        if (field.required) {
          criticalMissing.push(field.name);
        }
      }
    });
    
    // **CALCULER LE POURCENTAGE DE LA SECTION**
    const sectionPercentage = sectionMaxScore > 0 ? (sectionScore / sectionMaxScore) * 100 : 0;
    completedSections[section] = {
      percentage: Math.round(sectionPercentage),
      weight: config.weight,
      missing: sectionMissing,
      score: sectionScore,
      maxScore: sectionMaxScore
    };
    
    // **AJOUTER AU SCORE TOTAL PONDÉRÉ**
    if (config.weight > 0) {
      totalScore += (sectionPercentage / 100) * config.weight;
      maxScore += config.weight;
      missingFields = missingFields.concat(sectionMissing);
    }
  });
  
  // **CALCULER LE POURCENTAGE TOTAL**
  const totalPercentage = maxScore > 0 ? Math.round(totalScore) : 0;
  
  // **VÉRIFICATIONS SPÉCIALES POUR LES ALERTES**
  const hasNoProbe = client.servers?.some(s => !isBooleanDefined(s.probeDeployed) || s.probeDeployed === false);
  const hasUnoptimizedDB = client.servers?.some(s => !isBooleanDefined(s.dbOptimized) || s.dbOptimized === false);
  const hasIncompleteServerInfo = serverAnalysis.incompleteServers.length > 0;
  const hasCriticalServerIssues = serverAnalysis.criticalIssues.length > 0;
  
  // **DÉTERMINER LE STATUT ET LA COULEUR (avec priorité aux problèmes critiques)**
  let status, color, icon, bgColor, borderColor, priority = 'normal';
  
  // **PRIORITÉ CRITIQUE : Si des éléments critiques manquent**
  if (hasCriticalServerIssues || criticalMissing.length > 0) {
    status = 'Critique';
    color = 'text-red-600';
    bgColor = 'bg-red-50';
    borderColor = 'border-red-200';
    icon = '⚠️';
    priority = 'critical';
  }
  // **PRIORITÉ IMPORTANTE : Sonde ou BDD non configurées**
  else if (hasNoProbe || hasUnoptimizedDB) {
    status = 'Important';
    color = 'text-orange-600';
    bgColor = 'bg-orange-50';
    borderColor = 'border-orange-200';
    icon = '🟠';
    priority = 'important';
  }
  // **ANALYSER LE POURCENTAGE NORMAL**
  else if (totalPercentage === 100) {
    status = 'Complet';
    color = 'text-green-600';
    bgColor = 'bg-green-50';
    borderColor = 'border-green-200';
    icon = '✅';
  } else if (totalPercentage >= 80) {
    status = 'Presque complet';
    color = 'text-blue-600';
    bgColor = 'bg-blue-50';
    borderColor = 'border-blue-200';
    icon = '🔵';
  } else if (totalPercentage >= 60) {
    status = 'Partiel';
    color = 'text-yellow-600';
    bgColor = 'bg-yellow-50';
    borderColor = 'border-yellow-200';
    icon = '🟡';
  } else if (totalPercentage >= 30) {
    status = 'Incomplet';
    color = 'text-orange-600';
    bgColor = 'bg-orange-50';
    borderColor = 'border-orange-200';
    icon = '🟠';
  } else {
    status = 'Minimal';
    color = 'text-red-600';
    bgColor = 'bg-red-50';
    borderColor = 'border-red-200';
    icon = '🔴';
  }
  
  // **DÉTAILS SPÉCIFIQUES AVEC ANALYSE COMPLÈTE**
  const details = {
    hasServers: client.servers && client.servers.length > 0,
    serverCount: client.servers?.length || 0,
    hasNoProbe,
    hasUnoptimizedDB,
    hasIncompleteServerInfo,
    hasCriticalServerIssues,
    hasProcedure: !isEmpty(client.connectionProcedure),
    hasTelemaintenance: client.telemaintenance?.enabled,
    hasBastion: client.bastionConfig?.enabled,
    hasUrls: !!(client.patientPortalUrl || client.carePortalUrl),
    serverAnalysis: serverAnalysis
  };
  
  return {
    percentage: totalPercentage,
    status,
    color,
    bgColor,
    borderColor,
    icon,
    priority,
    sections: completedSections,
    missingFields: [...new Set(missingFields)],
    criticalMissing: [...new Set(criticalMissing)],
    details,
    serverAnalysis,
    summary: {
      total: missingFields.length + serverAnalysis.warningIssues.length,
      critical: criticalMissing.length + serverAnalysis.criticalIssues.length,
      warnings: serverAnalysis.warningIssues.length,
      hasAlerts: hasNoProbe || hasUnoptimizedDB || hasIncompleteServerInfo,
      hasCriticalAlerts: hasCriticalServerIssues || criticalMissing.length > 0,
      incompleteServersCount: serverAnalysis.incompleteServers.length,
      serverCompletionScore: serverAnalysis.completionScore,
      totalServerFields: serverAnalysis.totalServerFields,
      completedServerFields: serverAnalysis.completedServerFields
    }
  };
}

// Fonction pour obtenir un résumé rapide de complétion
export function getCompletionBadge(percentage) {
  if (percentage === 100) {
    return { text: '100%', class: 'bg-green-100 text-green-800 border-green-300' };
  } else if (percentage >= 80) {
    return { text: `${percentage}%`, class: 'bg-blue-100 text-blue-800 border-blue-300' };
  } else if (percentage >= 60) {
    return { text: `${percentage}%`, class: 'bg-yellow-100 text-yellow-800 border-yellow-300' };
  } else if (percentage >= 30) {
    return { text: `${percentage}%`, class: 'bg-orange-100 text-orange-800 border-orange-300' };
  } else {
    return { text: `${percentage}%`, class: 'bg-red-100 text-red-800 border-red-300' };
  }
}

// Fonction pour générer un tooltip de complétion
export function getCompletionTooltip(completion) {
  const sections = [];
  
  // Sections de base
  if (completion.sections.basic) {
    sections.push(`Infos de base: ${completion.sections.basic.percentage}%`);
  }
  if (completion.sections.servers && completion.details.hasServers) {
    sections.push(`Serveurs: ${completion.sections.servers.percentage}%`);
  }
  if (completion.sections.urls) {
    sections.push(`URLs: ${completion.sections.urls.percentage}%`);
  }
  if (completion.sections.procedures) {
    sections.push(`Procédures: ${completion.sections.procedures.percentage}%`);
  }
  
  // Alertes critiques
  const criticalAlerts = [];
  if (completion.details.hasNoProbe) {
    criticalAlerts.push('⚠️ Sonde manquante');
  }
  if (completion.details.hasUnoptimizedDB) {
    criticalAlerts.push('⚠️ BDD non optimisée');
  }
  if (completion.serverAnalysis.criticalIssues.length > 0) {
    criticalAlerts.push(`⚠️ ${completion.serverAnalysis.criticalIssues.length} élément(s) critique(s) serveur`);
  }
  
  // Warnings serveur
  if (completion.serverAnalysis.warningIssues.length > 0) {
    sections.push(`🟡 ${completion.serverAnalysis.warningIssues.length} élément(s) à compléter`);
  }
  
  // Ajouter les alertes critiques en rouge
  if (criticalAlerts.length > 0) {
    sections.push('');
    sections.push(...criticalAlerts);
  }
  
  // Informations sur les serveurs incomplets
  if (completion.serverAnalysis.incompleteServers.length > 0) {
    sections.push('');
    sections.push(`${completion.serverAnalysis.incompleteServers.length} serveur(s) incomplet(s):`);
    completion.serverAnalysis.incompleteServers.forEach(server => {
      const issues = server.critical.length + server.warnings.length;
      const criticalBadge = server.critical.length > 0 ? ' ⚠️' : '';
      sections.push(`  • ${server.name}: ${issues} problème(s)${criticalBadge}`);
    });
  }
  
  return sections.join('\n');
}
